"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

const HERO_IMAGE =
	"/image/new-version/casting/Low-Pressure-Die-Casting/low-pressure-die-casting-with-js-precision.webp";

const FEATURES = [
	{ titleKey: "lpdcJsPrecisionFeature1Title", descKey: "lpdcJsPrecisionFeature1Desc" },
	{ titleKey: "lpdcJsPrecisionFeature2Title", descKey: "lpdcJsPrecisionFeature2Desc" },
	{ titleKey: "lpdcJsPrecisionFeature3Title", descKey: "lpdcJsPrecisionFeature3Desc" },
] as const;

const GlobeIcon = ({ className }: { className?: string }) => (
	<svg
		className={`h-14 w-14 sm:h-16 sm:w-16 md:h-[72px] md:w-[72px] lg:h-20 lg:w-20 ${className ?? ""}`}
		viewBox="0 0 48 48"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden
	>
		<circle cx="24" cy="24" r="20" />
		<ellipse cx="24" cy="24" rx="20" ry="8" />
		<ellipse cx="24" cy="24" rx="8" ry="20" />
	</svg>
);

const CheckCircleIcon = () => (
	<svg
		className="mt-0.5 h-5 w-5 flex-shrink-0 sm:h-6 sm:w-6"
		viewBox="0 0 24 24"
		fill="none"
		aria-hidden="true"
	>
		<circle cx="12" cy="12" r="11" fill="#64a525" />
		<path
			d="M7.5 12.3L10.5 15.2L16.5 8.8"
			stroke="#ffffff"
			strokeWidth="2.2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

export default function LowPressureDieCastingWithJsPrecision() {
	const t = useTranslations("new-low-pressure-casting");

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="relative mb-8 flex min-h-[72px] items-center sm:mb-10 sm:min-h-[80px] md:mb-12 md:min-h-[88px]">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						<span className="block">{t("lpdcJsPrecisionTitleLine1")}</span>
						<span className="block">{t("lpdcJsPrecisionTitleLine2")}</span>
					</h2>
					<div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center">
						<GlobeIcon className="text-themePrimary400" />
					</div>
				</div>

				<div className="flex flex-col gap-8 lg:flex-row lg:items-stretch lg:gap-8 xl:gap-10">
					<div className="flex w-full flex-col lg:min-w-0 lg:flex-1">
						<div className="relative aspect-[4/3] w-full overflow-hidden shadow-lg lg:aspect-auto lg:h-full lg:min-h-[410px]">
							<Image
								src={HERO_IMAGE}
								alt={t("lpdcJsPrecisionImageAlt")}
								fill
								className="object-cover"
								sizes="(max-width: 1024px) 100vw, 50vw"
							/>
						</div>
					</div>

					<div className="flex w-full min-w-0 flex-1 flex-col justify-center lg:min-w-0">
						<div className="mb-8 space-y-5">
							{FEATURES.map((item) => (
								<div key={item.titleKey} className="flex items-start gap-3">
									<CheckCircleIcon />
									<div className="min-w-0 flex-1">
										<h3 className="text-base font-semibold text-black sm:text-lg">{t(item.titleKey)}</h3>
										<p className="mt-2 text-sm leading-relaxed text-black md:text-base">{t(item.descKey)}</p>
									</div>
								</div>
							))}
						</div>

						<Link
							href="/upload"
							className="inline-flex w-fit items-center justify-center rounded bg-themePrimary400 px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white md:px-8 md:py-3.5 md:text-base"
						>
							{t("lpdcJsPrecisionGetQuote")}
						</Link>
					</div>
				</div>
			</div>
		</section>
	);
}
